/**
 * The catalog's filter row: the view toggle, search, Kind, Status (with `asset.view.draft`) and Maturity, then
 * Industry, Partner, Country and Freshness. Below 720px those four fold behind **More filters** (a CSS swap, so wide
 * screens always show them), and the toolbar ends with **New asset** for managers.
 */

import { Link } from 'react-router-dom';
import { ChevronDown, LayoutGrid, List, Plus, Search, SlidersHorizontal } from 'lucide-react';

import type { AssetSort } from '@/api/types';
import { FilterSelect } from '@/ui/FilterSelect';
import { MultiSelect } from '@/ui/MultiSelect';
import { ViewToggle } from '@/ui/ViewToggle';

import { KIND_OPTIONS, MATURITY_OPTIONS, STATUS_OPTIONS } from '../assetUi';
import { effectiveSort, moreFilterCount } from './catalogParams';
import type { CatalogLayout, CatalogPageState } from './useCatalogPage';

const LAYOUT_OPTIONS = [
  { key: 'list' as CatalogLayout, label: 'List', Icon: List },
  { key: 'tiles' as CatalogLayout, label: 'Tiles', Icon: LayoutGrid },
];

const SORT_OPTIONS: { value: AssetSort; label: string }[] = [
  { value: 'relevance', label: 'Sort by relevance' },
  { value: 'name', label: 'Sort by name' },
  { value: 'kind', label: 'Sort by kind' },
  { value: 'updated', label: 'Sort by last update' },
  { value: 'confirmed', label: 'Sort by last confirmed' },
];

const FRESHNESS_OPTIONS = [
  { value: 'true', label: 'Stale' },
  { value: 'false', label: 'Not stale' },
];

function MoreFilters({ state }: { state: CatalogPageState }) {
  const { view, update } = state;
  return (
    <div id="catalog-more-filters" className={`catalog-more${state.moreOpen ? ' is-open' : ''}`}>
      {state.industries.length > 0 && (
        <MultiSelect
          label="Filter by industry"
          allLabel="All industries"
          noun="industries"
          searchable
          options={state.industries.map((i) => ({ value: i.id, label: i.name }))}
          value={view.industry}
          onChange={(industry) => update({ industry })}
        />
      )}
      <MultiSelect
        label="Filter by partner"
        allLabel="All partners"
        noun="partners"
        searchable
        options={state.partners.map((p) => ({ value: p.id, label: p.name }))}
        value={view.partner}
        onChange={(partner) => update({ partner })}
      />
      <MultiSelect
        label="Filter by country"
        allLabel="All countries"
        noun="countries"
        searchable
        options={state.countries.map((c) => ({ value: c.code, label: c.name }))}
        value={view.country}
        onChange={(country) => update({ country })}
      />
      <FilterSelect
        label="Filter by freshness"
        allLabel="Any freshness"
        value={view.stale === null ? null : String(view.stale)}
        options={FRESHNESS_OPTIONS}
        onChange={(value) => update({ stale: value === null ? null : value === 'true' })}
      />
    </div>
  );
}

function SortSelect({ state }: { state: CatalogPageState }) {
  const options = state.view.q ? SORT_OPTIONS : SORT_OPTIONS.filter((o) => o.value !== 'relevance');
  return (
    <span className="select-wrap">
      <select
        className="toolbar-select"
        aria-label="Sort by"
        value={effectiveSort(state.view)}
        onChange={(e) => state.update({ sort: e.target.value as AssetSort, order: null })}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <ChevronDown aria-hidden="true" />
    </span>
  );
}

export function CatalogToolbar({ state }: { state: CatalogPageState }) {
  const { view, update } = state;
  const more = moreFilterCount(view);
  return (
    <div className="toolbar filter-panel catalog-toolbar">
      <ViewToggle label="Catalog view" options={LAYOUT_OPTIONS} value={state.layout} onChange={state.setLayout} />
      <span className="filter-divider" aria-hidden="true" />
      <div className="search-field">
        <Search aria-hidden="true" />
        <input
          type="search"
          value={state.query}
          placeholder="Search assets"
          aria-label="Search assets"
          onChange={(e) => state.setQuery(e.target.value)}
        />
      </div>
      <MultiSelect label="Filter by kind" allLabel="All kinds" noun="kinds" options={KIND_OPTIONS} value={view.kind} onChange={(kind) => update({ kind })} />
      {state.canDrafts && (
        <MultiSelect
          label="Filter by status"
          allLabel="Draft and published"
          noun="statuses"
          options={STATUS_OPTIONS}
          value={view.status}
          onChange={(status) => update({ status })}
        />
      )}
      <MultiSelect
        label="Filter by maturity"
        allLabel="All maturities"
        noun="maturities"
        options={MATURITY_OPTIONS}
        value={view.maturity}
        onChange={(maturity) => update({ maturity })}
      />
      <button
        type="button"
        className="btn btn-secondary catalog-more-toggle"
        aria-expanded={state.moreOpen}
        aria-controls="catalog-more-filters"
        onClick={state.toggleMore}
      >
        <SlidersHorizontal aria-hidden="true" /> More filters{more > 0 ? ` (${more})` : ''}
      </button>
      <MoreFilters state={state} />
      {state.layout === 'tiles' && <SortSelect state={state} />}
      {state.canCreate && (
        <span className="toolbar-primary">
          <Link className="btn btn-primary" to="/assets/new">
            <Plus aria-hidden="true" /> New asset
          </Link>
        </span>
      )}
    </div>
  );
}
